// QR Code display — canvas-based via lib `qrcode`.
// Fase 4A.7-C: mostra QR pra user escanear no celular e aprovar pareamento.
//
// Performance: a lib `qrcode` (~25KB) é carregada SOB DEMANDA (`await import`)
// dentro do effect — não mais eager. Antes ela entrava no boot porque o
// TrialGate/AccountGate importam QRDisplay cedo; agora o chunk `qrcode` só é
// buscado quando um QR de fato vai renderizar. Render via Canvas 2D (compatível
// com Tizen 2017, sem Workers). 1× render no mount, sem re-render no polling.
// Enquanto o chunk carrega, um skeleton (tier-aware) ocupa o espaço.

import { useTranslation } from '@app-iptv/i18n'
import { useEffect, useMemo, useRef, useState } from 'react'

import { readQrSize } from '../lib/card-metrics'

import qrStyles from './QRDisplay.module.css'

interface QRDisplayProps {
  /** Payload que vira QR (URL absoluta, ex: https://swipetvplayer.com/parear/XXXX-XXXX). */
  value: string
  /**
   * Tamanho em px. OMITIR é o caminho normal: sem este prop o QR sai do TOKEN
   * `--qr-size` (tier-aware, canvas-aware — ver readQrSize/index.css).
   *
   * ===== NÃO CRAVE NÚMERO AQUI =====
   * Todo QR deste app é escaneado do SOFÁ, a 2,5-3m, e o spec
   * (docs/specs/onboarding-entrada-tv-ux.md) fixa piso de 360px na TV. Os dois
   * call sites que cravavam (`size={240}` no layout enxuto do PairingShell e
   * `size={200}` no TrialOverlay) ficaram ABAIXO do piso e sobreviveram
   * intactos à leva que consertou o gate — era a MESMA queixa do dono ("que QR
   * code pequeno") viva ao lado do conserto. `no-hardcoded-qr-size.test.ts`
   * reprova literal novo.
   *
   * Sobra pra teste (que precisa de tamanho determinístico sem DOM) e pro caso
   * legítimo de um QR que comprovadamente não caiba no token — nesse caso
   * documente o pixel medido, não o palpite.
   */
  size?: number
  className?: string
  /** Rótulo acessível. Default genérico ("QR code") — passe um mais específico
   *  por contexto (pareamento vs compra) se quiser. */
  ariaLabel?: string
}

export function QRDisplay({ value, size: sizeProp, className, ariaLabel }: QRDisplayProps) {
  const { t } = useTranslation()
  // Token por padrão. `useMemo` porque readQrSize toca o DOM (sonda) — embora
  // ela já seja memoizada em card-metrics, não há razão de reavaliar por render.
  const size = useMemo(() => (sizeProp != null ? sizeProp : Math.round(readQrSize())), [sizeProp])
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Fallback visível quando o canvas NÃO renderiza (lib falha, contexto 2D
  // indisponível, navegador exótico). Sem isso o usuário via um quadrado em
  // branco/preto sem pista — agora cai numa mensagem legível apontando o
  // caminho sem câmera (digitar o código no site, que está logo abaixo no gate).
  const [failed, setFailed] = useState(false)
  // `true` enquanto o chunk `qrcode` (lazy) carrega + o canvas é desenhado. O
  // skeleton (sobreposto ao canvas) some quando o QR pinta OU quando cai no
  // fallback textual. Começa `true`: o import é assíncrono, então há sempre uma
  // janela (curta) antes do 1º desenho.
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false
    setFailed(false)
    setLoading(true)
    // Import DINÂMICO da lib: o chunk `qrcode` só é buscado aqui (fora do boot).
    // `default` espelha o `import QRCode from 'qrcode'` anterior (a lib expõe os
    // renderers como export default sob esModuleInterop).
    void (async () => {
      try {
        // `default` espelha o `import QRCode from 'qrcode'` estático anterior: sob
        // esModuleInterop a lib (CJS) expõe os renderers no `default`, e é o que o
        // compilado lê (`i = o.default; i.toCanvas(...)`) — mesmo interop do import
        // estático provado em produção.
        const { default: QRCode } = await import('qrcode')
        if (cancelled) return
        // Margin 4 = a ZONA DE SILÊNCIO mínima da ISO/IEC 18004. Era 1, com a
        // justificativa de que "o default 4 desperdiça espaço em TV" — mas a
        // quiet zone não é margem estética: é o que permite ao decodificador
        // achar os padrões de detecção contra o fundo. Com 1 módulo, leitores
        // encostam o código no fundo escuro do gate e falham de forma
        // intermitente (o pior modo: às vezes lê).
        //
        // Não é trade-off com o tamanho — as duas melhoram JUNTAS. Matriz real
        // 29x29: antes, 220px com margin 1 dava ~7,1px/módulo; agora, 440px com
        // margin 4 dá ~11,9px/módulo. Mais denso E com a zona de silêncio certa.
        //
        // Cores no padrão CANÔNICO de QR (módulos ESCUROS sobre fundo CLARO) —
        // scanners de celular esperam isso; invertido (claro/escuro) falhava em
        // vários leitores. Error correction 'M' tolera foto torta / reflexo de
        // tela de TV melhor que 'L'.
        await QRCode.toCanvas(canvas, value, {
          width: size,
          margin: 4,
          errorCorrectionLevel: 'M',
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })
        if (cancelled) return
        setLoading(false)
      } catch (err: unknown) {
        if (cancelled) return
        // Cobre tanto falha do `await import` (chunk não carregou) quanto do
        // `toCanvas` (render). Ambos caem no fallback textual sem câmera.
        console.error('[QRDisplay] failed to render', err)
        setFailed(true)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [value, size])

  if (failed) {
    // Degrada bem: o QR não pôde ser desenhado. Mostra a instrução textual no
    // mesmo espaço (caminho sem câmera). Mantém o aria-label pra leitores.
    return (
      <div
        role="img"
        aria-label={ariaLabel ?? t('common.qrCodeAria')}
        className={className}
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 16,
          boxSizing: 'border-box',
          borderRadius: 12,
          background: '#fff',
          color: '#000',
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        {t('common.qrUnavailable')}
      </div>
    )
  }

  // Container relativo: o canvas SEMPRE fica no DOM (o `toCanvas` precisa do ref);
  // enquanto o chunk carrega, o skeleton fica sobreposto (position:absolute,
  // top/left explícitos — NÃO `inset`, que é Chr87+ e some no piso). Callers hoje
  // não passam `className` (envolvem o QR na própria caixa), mas mantemos o prop
  // no container por compat.
  return (
    <div className={className} style={{ position: 'relative', width: size, height: size }}>
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        aria-label={ariaLabel ?? t('common.qrCodeAria')}
        style={{ borderRadius: 12, display: 'block' }}
      />
      {loading ? (
        <div aria-hidden="true" className={qrStyles.skeleton} style={{ width: size, height: size }} />
      ) : null}
    </div>
  )
}
